import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ResponsiveDialog, {
  StyledButton,
  StyledLoadingButton,
} from '~/components/ResponsiveDialog';
import {
  generateVPSAppButtonTestId,
  getSelfManagedPlanName,
  isSvgOrPng,
} from '~/Nova/scenes/HostingList/utils/helpers';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';

import LetterAvatar from '../../../components/LetterAvatar';
import { useReimageMutation } from '../../../hooks/useReimageMutation';
import {
  CHANGE_IMAGE,
  CPANEL_APP,
  VPS_REIMAGE_ACTION_TYPE,
} from '../../../utils/constants';
import { getLogoConfig } from '../../../utils/imageLogos';

interface ReImageAppsModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  selectedApp: string;
  hostingId: string;
  osAdvancedImages?: PublicImageItem[];
  isSelfManagedDedi?: boolean;
}

const ReImageAppsModal = ({
  open,
  setOpen,
  selectedApp,
  hostingId,
  osAdvancedImages = [],
  isSelfManagedDedi = false,
}: ReImageAppsModalProps) => {
  const { t } = useTranslation('hosting', {
    keyPrefix: 'hostingNova.selfManagedApps',
  });
  const [confirmationText, setConfirmationText] = useState('');

  // Derive app display info from the osAdvancedImages list
  const selectedAppImage = osAdvancedImages.find(
    (img) =>
      img.app === selectedApp ||
      img.software?.toLowerCase() === selectedApp.toLowerCase(),
  );

  const { title, os_name, software, id: imageId } = selectedAppImage ?? {};
  const appName = title ?? selectedApp;
  const { logo = '', logoAlt = '' } = getLogoConfig(selectedApp) ?? {};
  const defaultDockerImage = os_name;

  const handleConfirmationText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
  };

  const handleClose = useCallback(() => {
    setConfirmationText('');
    setOpen(false);
  }, [setOpen]);

  const { mutate: handleAppReimage, isLoading: isLoadingReimage } =
    useReimageMutation({
      hostingId,
      onClose: handleClose,
    });

  const handleProceed = () => {
    handleAppReimage({
      hostingId,
      data: {
        action: VPS_REIMAGE_ACTION_TYPE,
        image_id: imageId,
        ...(software === 'docker' && {
          type: 'docker',
          subtype: selectedApp,
        }),
      },
    });
  };

  const isConfirmed = confirmationText.toLowerCase() === CHANGE_IMAGE;
  const isSvgOrPngLogo = isSvgOrPng(logo);
  const logoSize = { width: 40, height: 40 };

  return (
    <ResponsiveDialog open={open} data-testid="reImage-apps-modal">
      <DialogTitle>
        <Typography variant="h2">{t('reimageServer')}</Typography>
        <Divider sx={{ mt: 1 }} />
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            {isSvgOrPngLogo ? (
              <Box
                component="img"
                src={logo}
                alt={logoAlt}
                width={logoSize.width}
                height={logoSize.height}
              />
            ) : (
              <LetterAvatar letters={logo} width={40} height={40} />
            )}
            <Typography variant="h3">
              {appName && defaultDockerImage
                ? `${appName} on ${defaultDockerImage}`
                : appName}
            </Typography>
          </Stack>
          <Stack direction="column" spacing={3} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1}>
              <WarningIcon color="error" />

              <Stack spacing={2}>
                <Typography component="div">
                  <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                    {t('modal.warning')}
                  </Typography>
                  {t('modal.warningTextOS', {
                    hostingPlanType: getSelfManagedPlanName(isSelfManagedDedi),
                  })}
                </Typography>

                {selectedApp === CPANEL_APP && (
                  <Typography>
                    {t('modal.cpanelLicenseRequiredMessage')}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <TextField
              fullWidth
              label={t('modal.changeImageConfirmationText')}
              value={confirmationText}
              onChange={handleConfirmationText}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          spacing={2}
          flexGrow={1}
          justifyContent="flex-end"
        >
          <StyledButton
            variant="outlined"
            onClick={handleClose}
            data-testid={generateVPSAppButtonTestId(appName, 'cancel')}
          >
            {t('modal.cancel')}
          </StyledButton>
          <StyledLoadingButton
            data-testid={generateVPSAppButtonTestId(appName, 'proceed')}
            onClick={handleProceed}
            color="error"
            disabled={!isConfirmed || isLoadingReimage}
            loading={isLoadingReimage}
          >
            {t('modal.proceed')}
          </StyledLoadingButton>
        </Stack>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ReImageAppsModal;
